<template>
  <v-dialog v-model="dialog" max-width="600px" persistent @keydown="handleKeyboard">
    <v-card class="scanner-card">
      <v-card-title class="scanner-header">
        <span class="header-title">
          <v-icon left dark small>mdi-scan</v-icon>
          Scan {{ currentDocTypeLabel }}
        </span>
        <v-spacer></v-spacer>
        <v-btn icon dark small @click="close" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="scanner-body">
        <div class="steps">
          <span class="step" :class="{ active: scanState === 'positioning' }">1</span>
          <span class="step-line"></span>
          <span class="step" :class="{ active: scanState === 'preview' }">2</span>
          <span class="step-line"></span>
          <span class="step" :class="{ active: scanState === 'verified' }">3</span>
        </div>

        <div v-if="guest" class="guest-info">
          <span class="guest-name">{{ guest.payment_person || guest.name }}</span>
          <span class="guest-badge">{{ guestResidencyType || 'Visitor' }}</span>
        </div>

        <div class="doc-tabs">
          <button 
            v-for="type in availableDocTypes"
            :key="type.value"
            :class="['doc-tab', { active: currentDocType === type.value }]"
            @click="setDocType(type.value)"
          >
            <v-icon small>mdi-{{ type.icon }}</v-icon>
            {{ type.label }}
          </button>
        </div>

        <div v-if="currentDocType === 'work_permit'" class="mode-toggle">
          <button :class="['mode-btn', { active: scanMode === 'qr' }]" @click="switchToQRMode">QR</button>
          <button :class="['mode-btn', { active: scanMode === 'camera' }]" @click="switchToCameraMode">Camera</button>
        </div>

        <div v-if="scanMode === 'qr' && currentDocType === 'work_permit'">
          <QRScanner 
            v-if="showQRScanner"
            ref="qrScanner"
            :guest="guest"
            :guest-key="guestKey"
            :booking-id="bookingId || (guest ? guest.booking_id : null)"
            @verified="onQRVerified"
            @error="onQRVerificationError"
            @scanning="onQRScanning"
          />
        </div>

        <div v-if="scanMode === 'camera'" class="camera-section">
          <div class="camera-view" :class="{ error: cameraError, success: scanState === 'verified' }">
            <video ref="video" autoplay playsinline v-show="cameraActive && scanState !== 'preview'"></video>
            <img v-if="scanState === 'preview' && previewImage" :src="previewImage" class="preview-img" />
            <canvas ref="canvas" style="display:none"></canvas>
            
            <div v-if="cameraActive || scanState === 'preview'" class="frame-overlay" :class="{ verified: scanState === 'verified' }">
              <div class="corner tl"></div>
              <div class="corner tr"></div>
              <div class="corner bl"></div>
              <div class="corner br"></div>
              <div v-if="scanState === 'positioning'" class="scan-line"></div>
            </div>
            
            <div v-if="!cameraActive && scanState === 'positioning'" class="loading-state">
              <v-icon size="36" color="primary">mdi-camera</v-icon>
              <p>Initializing camera...</p>
            </div>

            <div v-if="cameraError && scanState !== 'failed'" class="error-overlay">
              <span>{{ cameraError }}</span>
              <button @click="retryScan">Retry</button>
            </div>

            <div v-if="scanState === 'positioning' && cameraActive" class="capture-hint">
              <v-icon small>mdi-camera</v-icon>
              Tap or press Space
            </div>
          </div>

          <div v-if="scanState === 'positioning' && cameraActive && !cameraError" class="status-hint">
            <span :class="{ warn: !brightnessOk }">{{ brightnessOk ? '✓ Good lighting' : '⚠ Adjust lighting' }}</span>
            <span>📐 Frame document</span>
          </div>

          <div v-if="scanState === 'preview'" class="action-row">
            <button class="btn secondary" @click="retakeScan">
              <v-icon small>mdi-refresh</v-icon> Retake <kbd class="inline">R</kbd>
            </button>
            <button class="btn primary" @click="verifyScan" :disabled="isScanning">
              <v-icon small>mdi-check</v-icon> Use <kbd class="inline">U</kbd>
              <v-progress-circular v-if="isScanning" indeterminate size="16" width="2"></v-progress-circular>
            </button>
          </div>

          <div v-if="scanState === 'positioning' && cameraActive" class="action-row">
            <button class="btn secondary small" @click="close">
              <v-icon small>mdi-close</v-icon> Cancel
            </button>
            <button class="btn capture-btn" @click="captureDocument" :disabled="!cameraActive || isScanning">
              <v-icon>mdi-camera</v-icon> Capture <kbd class="inline">Space</kbd>
            </button>
          </div>

          <div v-if="scanState === 'verifying'" class="verifying-state">
            <v-progress-circular indeterminate size="20" width="2"></v-progress-circular>
            <span>Verifying document...</span>
          </div>

          <div v-if="scanState === 'verified'" class="success-state">
            <v-icon large color="success">mdi-check-circle</v-icon>
            <span class="success-title">Verified Successfully</span>
            <span class="success-detail">{{ currentDocTypeLabel }} confirmed</span>
            <span class="success-hint">Press <kbd>Enter</kbd> to confirm</span>
          </div>

          <div v-if="scanState === 'failed'" class="failed-state">
            <v-icon large color="error">mdi-alert-circle</v-icon>
            <span class="error-title">Verification Failed</span>
            <span class="error-detail">{{ getErrorMessage() }}</span>
            <div class="retry-row">
              <button class="btn retry-btn" @click="retryScan" :disabled="!canRetry">
                <v-icon small>mdi-refresh</v-icon> Retry ({{ retryCount }}/{{ maxRetries }})
              </button>
              <button class="btn secondary small" @click="close">
                <v-icon small>mdi-close</v-icon> Cancel
              </button>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="scanner-footer">
        <div class="footer-shortcuts">
          <span class="shortcut"><kbd>Space</kbd> Capture</span>
          <span class="shortcut"><kbd>R</kbd> Retake</span>
          <span class="shortcut"><kbd>U</kbd> Use</span>
          <span class="shortcut"><kbd>Enter</kbd> Confirm</span>
          <span class="shortcut"><kbd>Esc</kbd> Cancel</span>
        </div>
        <div class="footer-actions">
          <v-btn text small @click="close" class="cancel-btn">
            <v-icon small>mdi-close</v-icon> Cancel
          </v-btn>
          <v-btn 
            small 
            @click="confirmVerification" 
            :disabled="scanState !== 'verified'"
            class="confirm-btn"
          >
            <v-icon left small>mdi-check</v-icon> Confirm
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import QRScanner from './QRScanner.vue';

export default {
  name: 'DocumentScanner',
  components: { QRScanner },
  props: {
    value: { type: Boolean, default: false },
    guest: { type: Object, default: null },
    guestKey: { type: Number, default: null },
    docType: { type: String, default: 'passport' },
    bookingId: { type: String, default: null }
  },
  data() {
    return {
      dialog: this.value,
      currentDocType: this.docType,
      scanMode: 'camera',
      scanState: 'positioning',
      cameraActive: false,
      cameraError: null,
      isScanning: false,
      stream: null,
      video: null,
      canvas: null,
      ctx: null,
      previewImage: null,
      verificationResult: null,
      proof: null,
      verified: false,
      qrVerificationResult: null,
      brightnessOk: true,
      brightnessCheckInterval: null,
      documentId: null,
      extractedDocumentNumber: null,
      showQRScanner: false,
      retryCount: 0,
      maxRetries: 3
    };
  },
  computed: {
    guestResidencyType() {
      if (!this.guest) return null;
      var name = (this.guest.payment_person || '').toLowerCase();
      if (name.indexOf('citizen') !== -1 || name.indexOf('school driver') !== -1) return 'Citizen';
      if (name.indexOf('ea resident') !== -1) return 'EA Resident';
      if (name.indexOf('non resident') !== -1) return 'Non Resident';
      if (name.indexOf('resident') !== -1) return 'Resident';
      return null;
    },
    availableDocTypes() {
      var residency = this.guestResidencyType;
      if (residency === 'Citizen') {
        return [
          { value: 'kenyan_id', label: 'ID Card', icon: 'card-account-details' },
          { value: 'passport', label: 'Passport', icon: 'passport' }
        ];
      }
      if (residency === 'EA Resident' || residency === 'Resident') {
        return [
          { value: 'work_permit', label: 'Work Permit', icon: 'card-account-details' },
          { value: 'passport', label: 'Passport', icon: 'passport' }
        ];
      }
      return [{ value: 'passport', label: 'Passport', icon: 'passport' }];
    },
    currentDocTypeLabel() {
      var labels = { passport: 'Passport', kenyan_id: 'ID Card', work_permit: 'Work Permit' };
      return labels[this.currentDocType] || 'Document';
    },
    isQrVerified() {
      return this.qrVerificationResult && this.qrVerificationResult.verified === true;
    },
    canRetry() {
      return this.retryCount < this.maxRetries;
    }
  },
  watch: {
    value(val) {
      this.dialog = val;
      if (val) this.initScan();
      else this.cleanupQRScanner();
    },
    dialog(val) {
      if (!val) {
        this.cleanup();
        this.cleanupQRScanner();
        this.resetState();
        this.$emit('input', false);
      }
    },
    scanMode(val) {
      this.cleanup();
      this.cleanupQRScanner();
      this.resetState();
      var self = this;
      if (val === 'qr' && this.dialog && this.currentDocType === 'work_permit') {
        this.showQRScanner = true;
        this.$nextTick(function() {
          setTimeout(function() {
            if (self.$refs.qrScanner && self.$refs.qrScanner.startScanner) {
              self.$refs.qrScanner.startScanner();
            }
          }, 500);
        });
      } else if (val === 'camera') {
        this.showQRScanner = false;
        setTimeout(function() { self.startCamera(); }, 300);
      }
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyboard);
  },
  beforeDestroy() {
    this.cleanup();
    this.cleanupQRScanner();
    document.removeEventListener('keydown', this.handleKeyboard);
  },
  methods: {
    resetState() {
      this.scanState = 'positioning';
      this.previewImage = null;
      this.verificationResult = null;
      this.verified = false;
      this.proof = null;
      this.documentId = null;
      this.extractedDocumentNumber = null;
      this.qrVerificationResult = null;
      this.cameraError = null;
      this.isScanning = false;
      this.retryCount = 0;
    },
    switchToQRMode() {
      this.cleanup();
      this.resetState();
      this.scanMode = 'qr';
      this.showQRScanner = true;
    },
    switchToCameraMode() {
      this.cleanup();
      this.cleanupQRScanner();
      this.resetState();
      this.scanMode = 'camera';
      this.showQRScanner = false;
      var self = this;
      setTimeout(function() { self.startCamera(); }, 300);
    },
    initScan() {
      var self = this;
      this.$nextTick(function() {
        self.initElements();
        self.resetState();
        var available = self.availableDocTypes;
        if (available.length > 0 && self.currentDocType !== available[0].value) {
          self.currentDocType = available[0].value;
        }
        if (self.currentDocType === 'work_permit' && self.scanMode === 'qr') {
          self.showQRScanner = true;
          setTimeout(function() {
            if (self.$refs.qrScanner && self.$refs.qrScanner.startScanner) {
              self.$refs.qrScanner.startScanner();
            }
          }, 500);
        } else {
          self.scanMode = 'camera';
          self.showQRScanner = false;
          setTimeout(function() { self.startCamera(); }, 300);
        }
      });
    },
    initElements() {
      this.video = this.$refs.video;
      this.canvas = this.$refs.canvas;
      if (this.canvas) this.ctx = this.canvas.getContext('2d');
    },
    handleKeyboard(e) {
      if (!this.dialog) return;
      if (e.key === ' ' && this.scanState === 'positioning' && this.cameraActive) {
        e.preventDefault();
        this.captureDocument();
      }
      if ((e.key === 'r' || e.key === 'R') && this.scanState === 'preview') {
        e.preventDefault();
        this.retakeScan();
      }
      if ((e.key === 'u' || e.key === 'U') && this.scanState === 'preview') {
        e.preventDefault();
        this.verifyScan();
      }
      if (e.key === 'Enter' && this.scanState === 'verified') {
        e.preventDefault();
        this.confirmVerification();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      }
    },
    setDocType(type) {
      this.currentDocType = type;
      this.resetState();
      this.$emit('update:docType', type);
      this.cleanup();
      this.cleanupQRScanner();
      var self = this;
      if (type === 'work_permit' && this.scanMode === 'qr' && this.dialog) {
        this.showQRScanner = true;
        setTimeout(function() {
          if (self.$refs.qrScanner && self.$refs.qrScanner.startScanner) {
            self.$refs.qrScanner.startScanner();
          }
        }, 300);
      } else {
        this.scanMode = 'camera';
        this.showQRScanner = false;
        setTimeout(function() { self.startCamera(); }, 300);
      }
    },
    getErrorMessage() {
      if (this.verificationResult) {
        return this.verificationResult.error || this.verificationResult.message || 'Please try again';
      }
      return 'Please try again';
    },
    generateDocumentId(documentNumber, docType, serialNumber) {
      if (!documentNumber) return null;
      return serialNumber ? docType + '_' + documentNumber + '_' + serialNumber : docType + '_' + documentNumber;
    },
    onQRVerified(result) {
      this.qrVerificationResult = result;
      this.verified = true;
      this.scanState = 'verified';
      this.proof = this.generateProof(JSON.stringify(result));
      this.extractedDocumentNumber = result.document_number || result.permit_number || result.id_number || result.passport_number;
      this.documentId = this.generateDocumentId(this.extractedDocumentNumber, 'work_permit', result.serial_number);
      this.verificationResult = result;
      this.cleanupQRScanner();
      this.retryCount = 0;
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
    },
    onQRVerificationError(error) {
      this.scanState = 'failed';
      this.verificationResult = { success: false, error: error || 'QR scan failed' };
      this.cleanupQRScanner();
      this.retryCount++;
    },
    onQRScanning(val) {},
    cleanupQRScanner() {
      this.showQRScanner = false;
      if (this.$refs.qrScanner && this.$refs.qrScanner.stopScanner) {
        this.$refs.qrScanner.stopScanner();
      }
    },
    async startCamera() {
      var self = this;
      this.cleanup();
      this.cameraActive = false;
      this.cameraError = null;
      this.scanState = 'positioning';

      this.video = this.$refs.video;
      this.canvas = this.$refs.canvas;
      if (this.canvas) this.ctx = this.canvas.getContext('2d');

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.cameraError = 'Camera not supported';
        return;
      }

      var constraints = {
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 },
          frameRate: { ideal: 30 }
        }
      };

      try {
        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
        this.setupVideoStream();
      } catch (err) {
        try {
          this.stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: { facingMode: 'environment' }
          });
          this.setupVideoStream();
        } catch (finalErr) {
          this.cameraError = 'Camera access denied';
          this.cameraActive = false;
        }
      }
    },
    setupVideoStream() {
      var self = this;
      if (!this.video) {
        this.video = this.$refs.video;
        if (!this.video) {
          this.cameraError = 'Video element not found';
          return;
        }
      }

      this.video.srcObject = this.stream;
      this.video.setAttribute('playsinline', true);
      
      this.video.onloadedmetadata = function() {
        self.video.play().then(function() {
          self.cameraActive = true;
          if (self.canvas) {
            self.canvas.width = self.video.videoWidth || 1920;
            self.canvas.height = self.video.videoHeight || 1080;
          }
          self.startBrightnessCheck();
        }).catch(function(err) {
          self.cameraError = 'Failed to start video';
          self.cameraActive = false;
        });
      };
    },
    captureDocument() {
      if (!this.cameraActive || this.isScanning || !this.video) return;

      this.isScanning = true;

      try {
        this.captureToCanvas();
        this.scanState = 'preview';
        this.cleanup();
        if (navigator.vibrate) navigator.vibrate(50);
      } catch (e) {
        console.error('Capture error:', e);
        this.cameraError = 'Capture failed';
      } finally {
        this.isScanning = false;
      }
    },
    captureToCanvas() {
      if (!this.video || !this.canvas || !this.ctx) return;
      
      this.canvas.width = this.video.videoWidth || 1920;
      this.canvas.height = this.video.videoHeight || 1080;
      
      this.ctx.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
      
      this.previewImage = this.canvas.toDataURL('image/jpeg', 0.95);
    },
    startBrightnessCheck() {
      this.stopBrightnessCheck();
      var self = this;
      
      this.brightnessCheckInterval = setInterval(function() {
        if (!self.cameraActive || !self.video || self.scanState !== 'positioning') return;
        
        try {
          var checkCanvas = document.createElement('canvas');
          var ctx = checkCanvas.getContext('2d');
          checkCanvas.width = 100;
          checkCanvas.height = 100;
          
          ctx.drawImage(self.video, 0, 0, 100, 100);
          var imgData = ctx.getImageData(0, 0, 100, 100).data;
          var colorSum = 0;
          
          for (var i = 0; i < imgData.length; i += 4) {
            colorSum += (imgData[i] * 0.299) + (imgData[i+1] * 0.587) + (imgData[i+2] * 0.114);
          }
          
          var averageBrightness = colorSum / (100 * 100);
          self.brightnessOk = averageBrightness > 40;
          
        } catch (e) { }
      }, 1500);
    },
    stopBrightnessCheck() {
      if (this.brightnessCheckInterval) {
        clearInterval(this.brightnessCheckInterval);
        this.brightnessCheckInterval = null;
      }
    },
    retakeScan() {
      this.previewImage = null;
      this.scanState = 'positioning';
      this.verificationResult = null;
      this.verified = false;
      this.documentId = null;
      this.extractedDocumentNumber = null;
      this.retryCount = 0;
      if (!this.cameraActive) {
        this.startCamera();
      }
    },
    verifyScan() {
      if (!this.previewImage || this.isScanning) return;
      
      var self = this;
      this.isScanning = true;
      this.scanState = 'verifying';
      
      var imageBase64 = this.previewImage.split(',')[1];
      this.sendVerificationRequest(imageBase64);
    },
    sendVerificationRequest(imageBase64) {
      var self = this;
      var sessionId = localStorage.getItem('lkjhgfdsa');
      
      var actions = {
        passport: 'verifyPassportBase64',
        kenyan_id: 'verifyKenyanIdBase64',
        work_permit: 'verifyWorkPermitBase64'
      };
      
      var actionName = actions[this.currentDocType];
      var proof = this.generateProof(JSON.stringify({ 
        timestamp: Date.now(), 
        docType: this.currentDocType 
      }));

      this.$store.dispatch(actionName, { 
        image_base64: imageBase64, 
        session_id: sessionId,
        guest_key: this.guestKey,
        guest_name: this.guest ? this.guest.payment_person : null,
        booking_id: this.guest ? this.guest.booking_id : null,
        proof: proof,
        doc_type: this.currentDocType
      })
      .then(function(result) {
        self.isScanning = false;
        self.verificationResult = result;
        
        if (result.success) {
          var docNumber = result.document_number || result.id_number || result.permit_number || result.passport_number;
          self.extractedDocumentNumber = docNumber;
          self.documentId = self.generateDocumentId(docNumber, self.currentDocType, result.serial_number);
          self.verified = true;
          self.scanState = 'verified';
          self.proof = proof;
          self.retryCount = 0;
          if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
        } else {
          self.scanState = 'failed';
          self.retryCount++;
        }
      })
      .catch(function(error) {
        self.isScanning = false;
        self.verificationResult = { 
          success: false, 
          error: error.error || error.message || 'Verification failed' 
        };
        self.scanState = 'failed';
        self.retryCount++;
      });
    },
    generateProof(data) {
      var hash = 0;
      var str = data + Date.now() + Math.random();
      for (var i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash = hash & hash;
      }
      return '0x' + Math.abs(hash).toString(16).padStart(16, '0');
    },
    cleanup() {
      this.stopBrightnessCheck();
      if (this.stream) {
        this.stream.getTracks().forEach(function(t) { t.stop(); });
        this.stream = null;
      }
      if (this.video) {
        this.video.pause();
        this.video.srcObject = null;
      }
      this.cameraActive = false;
      this.cameraError = null;
    },
    retryScan() {
      if (!this.canRetry) {
        this.close();
        return;
      }
      
      if (this.scanMode === 'qr') {
        this.retryQRScan();
        return;
      }
      
      this.cleanup();
      this.scanState = 'positioning';
      this.verificationResult = null;
      this.previewImage = null;
      this.retryCount++;
      var self = this;
      setTimeout(function() { self.startCamera(); }, 300);
    },
    retryQRScan() {
      if (!this.canRetry) {
        this.close();
        return;
      }
      
      this.cleanup();
      this.cleanupQRScanner();
      this.scanState = 'positioning';
      this.verificationResult = null;
      this.previewImage = null;
      this.retryCount++;
      var self = this;
      
      this.showQRScanner = true;
      this.$nextTick(function() {
        setTimeout(function() {
          if (self.$refs.qrScanner && self.$refs.qrScanner.startScanner) {
            self.$refs.qrScanner.startScanner();
          }
        }, 300);
      });
    },
    confirmVerification() {
      if (this.scanState !== 'verified') return;
      var isVerified = this.verified || this.isQrVerified;
      if (isVerified && this.guest && this.guestKey !== undefined) {
        this.$emit('verified', {
          guest: this.guest,
          guestKey: this.guestKey,
          proof: this.proof,
          docType: this.currentDocTypeLabel,
          docTypeValue: this.currentDocType,
          timestamp: new Date().toISOString(),
          verified: isVerified,
          backendResult: this.verificationResult || this.qrVerificationResult || null,
          source: this.qrVerificationResult ? 'ecitizen_api' : 'ocr',
          residencyType: this.guestResidencyType,
          documentId: this.documentId,
          documentNumber: this.extractedDocumentNumber
        });
        this.close();
      }
    },
    close() {
      this.cleanup();
      this.cleanupQRScanner();
      this.dialog = false;
      this.$emit('input', false);
      this.resetState();
      this.isScanning = false;
      this.cameraError = null;
      this.qrVerificationResult = null;
    }
  }
};
</script>

<style scoped>
.scanner-card {
  background: #0d0d12 !important;
  border-radius: 16px !important;
  overflow: hidden;
  max-height: 98vh !important;
  display: flex !important;
  flex-direction: column !important;
}

.scanner-header {
  background: linear-gradient(135deg, #1a1a2e, #16213e) !important;
  color: #fff !important;
  padding: 14px 20px !important;
  min-height: 48px;
  flex-shrink: 0 !important;
}
.header-title {
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}
.close-btn {
  opacity: 0.6;
  transition: opacity 0.2s;
}
.close-btn:hover {
  opacity: 1;
}

.scanner-body {
  padding: 12px 16px !important;
  background: #0f0f15 !important;
  flex: 1 1 auto !important;
  overflow-y: auto !important;
  max-height: calc(100vh - 160px) !important;
  min-height: 200px !important;
}

.scanner-footer {
  background: #0f0f15 !important;
  border-top: 1px solid rgba(255,255,255,0.06) !important;
  padding: 8px 16px !important;
  flex-shrink: 0 !important;
  min-height: 52px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  flex-wrap: wrap !important;
  gap: 6px !important;
}
.footer-shortcuts {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.footer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.shortcut {
  font-size: 10px;
  color: #555;
}
kbd {
  background: #1a1a22;
  color: #888;
  padding: 1px 8px;
  border-radius: 3px;
  font-size: 9px;
  font-family: monospace;
  border: 1px solid #2a2a35;
}
kbd.inline {
  font-size: 8px;
  padding: 0px 5px;
  background: rgba(255,255,255,0.06);
  border-color: rgba(255,255,255,0.08);
}
.cancel-btn {
  color: #ccc !important;
  background: rgba(255,255,255,0.06) !important;
  padding: 4px 14px !important;
  border-radius: 6px !important;
  font-weight: 500 !important;
}
.cancel-btn:hover {
  color: #fff !important;
  background: rgba(255,255,255,0.12) !important;
}
.confirm-btn {
  background: #2e7d32 !important;
  color: #fff !important;
  font-weight: 600 !important;
  padding: 4px 18px !important;
  border-radius: 6px !important;
  text-transform: none !important;
  font-size: 12px !important;
  min-width: 90px !important;
}
.confirm-btn:disabled {
  opacity: 0.3 !important;
  cursor: not-allowed !important;
}
.confirm-btn:not(:disabled):hover {
  background: #1b5e20 !important;
}

.steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 12px;
}
.step {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #1e1e28;
  color: #555;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
}
.step.active {
  background: #1976d2;
  color: #fff;
}
.step-line {
  width: 28px;
  height: 2px;
  background: #1e1e28;
  margin: 0 2px;
}

.guest-info {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
  padding: 4px 12px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
}
.guest-name {
  color: #fff;
  font-weight: 600;
  font-size: 13px;
}
.guest-badge {
  background: rgba(25, 118, 210, 0.15);
  padding: 2px 12px;
  border-radius: 10px;
  color: #64b5f6;
  font-size: 11px;
}

.doc-tabs {
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}
.doc-tab {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 14px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: transparent;
  color: #777;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;
}
.doc-tab:hover {
  border-color: rgba(255, 255, 255, 0.12);
  color: #aaa;
}
.doc-tab.active {
  background: rgba(25, 118, 210, 0.12);
  border-color: rgba(25, 118, 210, 0.3);
  color: #64b5f6;
}

.mode-toggle {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-bottom: 10px;
}
.mode-btn {
  padding: 3px 16px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: transparent;
  color: #666;
  font-size: 11px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}
.mode-btn.active {
  background: rgba(25, 118, 210, 0.12);
  border-color: rgba(25, 118, 210, 0.3);
  color: #64b5f6;
}

.camera-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.camera-view {
  background: #000;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  aspect-ratio: 4/3;
  min-height: 180px;
}
.camera-view.error {
  border: 2px solid #f44336;
}
.camera-view.success {
  border: 2px solid #4caf50;
}
.camera-view video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scaleX(-1);
}
.preview-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.frame-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 86%;
  height: 78%;
  border: 2px solid rgba(0, 255, 100, 0.25);
  border-radius: 8px;
  pointer-events: none;
}
.frame-overlay.verified {
  border-color: #4caf50;
  border-width: 3px;
}
.corner {
  position: absolute;
  width: 14px;
  height: 14px;
  border-color: #00ff64;
  border-style: solid;
  border-width: 0;
}
.frame-overlay.verified .corner {
  border-color: #4caf50;
}
.tl { top: -1px; left: -1px; border-top-width: 2px; border-left-width: 2px; }
.tr { top: -1px; right: -1px; border-top-width: 2px; border-right-width: 2px; }
.bl { bottom: -1px; left: -1px; border-bottom-width: 2px; border-left-width: 2px; }
.br { bottom: -1px; right: -1px; border-bottom-width: 2px; border-right-width: 2px; }

.scan-line {
  position: absolute;
  left: 5%;
  width: 90%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #00ff64, transparent);
  animation: scanMove 2s ease-in-out infinite;
}
@keyframes scanMove {
  0%, 100% { top: 5%; }
  50% { top: 93%; }
}

.loading-state {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 12px;
  gap: 6px;
}

.error-overlay {
  position: absolute;
  bottom: 14px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(244, 67, 54, 0.92);
  color: #fff;
  padding: 4px 14px;
  border-radius: 6px;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.error-overlay button {
  background: rgba(255,255,255,0.2);
  border: none;
  color: #fff;
  padding: 2px 12px;
  border-radius: 4px;
  font-size: 10px;
  cursor: pointer;
}

.capture-hint {
  position: absolute;
  bottom: 14px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: #eee;
  padding: 4px 16px;
  border-radius: 16px;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 6px;
  pointer-events: none;
}

.status-hint {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 2px 0;
  font-size: 11px;
  color: #666;
}
.status-hint .warn {
  color: #ffa726;
}

.action-row {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 18px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}
.btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.btn.secondary {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #aaa;
}
.btn.secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.05);
}
.btn.primary {
  background: #1976d2;
  color: #fff;
}
.btn.primary:hover:not(:disabled) {
  background: #1565c0;
}
.btn.capture-btn {
  background: #fff;
  color: #111;
  padding: 6px 20px;
  font-size: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
}
.btn.capture-btn:hover:not(:disabled) {
  background: #f0f0f0;
}
.btn.small {
  padding: 4px 12px;
  font-size: 10px;
}
.btn.retry-btn {
  background: #d32f2f;
  color: #fff;
}
.btn.retry-btn:hover:not(:disabled) {
  background: #b71c1c;
}
.btn.retry-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.verifying-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px;
  background: rgba(255,255,255,0.03);
  border-radius: 6px;
  color: #aaa;
  font-size: 12px;
}

.success-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(76, 175, 80, 0.06);
  border-radius: 8px;
  border: 1px solid rgba(76, 175, 80, 0.15);
}
.success-state .success-title {
  font-size: 16px;
  font-weight: 600;
  color: #66bb6a;
  margin-top: 4px;
}
.success-state .success-detail {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}
.success-state .success-hint {
  font-size: 10px;
  color: #666;
  margin-top: 8px;
}
.success-state .success-hint kbd {
  background: #1a1a22;
  color: #888;
  padding: 1px 8px;
  border-radius: 3px;
  font-size: 9px;
  font-family: monospace;
  border: 1px solid #2a2a35;
}

.failed-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(244, 67, 54, 0.06);
  border-radius: 8px;
  border: 1px solid rgba(244, 67, 54, 0.15);
}
.failed-state .error-title {
  font-size: 15px;
  font-weight: 600;
  color: #ef5350;
  margin-top: 4px;
}
.failed-state .error-detail {
  font-size: 12px;
  color: #aaa;
  margin: 4px 0 10px;
}

.retry-row {
  display: flex;
  gap: 10px;
  margin-top: 4px;
}

@media (max-width: 600px) {
  .scanner-card {
    max-width: 100% !important;
    border-radius: 0 !important;
  }
  .scanner-body {
    padding: 8px 12px !important;
    max-height: calc(100vh - 130px) !important;
    min-height: 150px !important;
  }
  .scanner-header {
    padding: 10px 14px !important;
    min-height: 42px;
  }
  .header-title {
    font-size: 14px;
  }
  .scanner-footer {
    padding: 6px 12px !important;
    min-height: 44px !important;
  }
  .footer-shortcuts {
    gap: 6px;
  }
  .shortcut {
    font-size: 9px;
  }
  kbd {
    font-size: 8px;
    padding: 1px 6px;
  }
  .cancel-btn {
    font-size: 11px !important;
    padding: 3px 12px !important;
  }
  .confirm-btn {
    font-size: 11px !important;
    min-width: 80px !important;
    padding: 3px 14px !important;
  }
  .step {
    width: 24px;
    height: 24px;
    font-size: 11px;
  }
  .step-line {
    width: 20px;
  }
  .camera-view {
    min-height: 150px;
    aspect-ratio: 3/4;
  }
  .doc-tab {
    font-size: 10px;
    padding: 3px 10px;
  }
  .guest-name {
    font-size: 12px;
  }
  .guest-badge {
    font-size: 10px;
  }
  .btn {
    font-size: 10px;
    padding: 4px 14px;
  }
  .btn.capture-btn {
    font-size: 11px;
    padding: 5px 16px;
  }
  .btn.retry-btn {
    font-size: 10px;
    padding: 4px 12px;
  }
  .success-state .success-title {
    font-size: 14px;
  }
  .failed-state .error-title {
    font-size: 14px;
  }
  .success-state .success-hint {
    font-size: 9px;
  }
}

@media (max-width: 400px) {
  .scanner-body {
    padding: 6px 10px !important;
    min-height: 120px !important;
  }
  .camera-view {
    min-height: 120px;
  }
  .doc-tab {
    font-size: 9px;
    padding: 2px 8px;
  }
  .btn {
    font-size: 9px;
    padding: 3px 10px;
  }
  .btn.capture-btn {
    font-size: 10px;
    padding: 4px 12px;
    min-width: 50px;
  }
  .btn.retry-btn {
    font-size: 9px;
    padding: 3px 10px;
  }
  .cancel-btn {
    font-size: 10px !important;
    padding: 2px 10px !important;
  }
  .confirm-btn {
    font-size: 10px !important;
    min-width: 70px !important;
    padding: 2px 12px !important;
  }
  .shortcut {
    font-size: 8px;
  }
  kbd {
    font-size: 7px;
    padding: 1px 4px;
  }
}
</style>